// @flow
import React from "react";
import { css } from "styled-components";
import { useInView } from "react-intersection-observer";
import { media } from "@nested/rebrand";
import { ButtonLink } from "@nested/component-library";
import { ResponsiveImage } from "../../components/ResponsiveImage";
import { mediumParagraph } from "../../components/Typography/Paragraphs";
import {
  largeHeading,
  regularHeading,
  sectionHeading,
} from "../../components/Typography/Headings";
import { variableSliceAdjacentSliceRules } from "../AdjacentSliceRules/AdjacentSliceRules";

const backgroundStyle = css`
  background-color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.hague : "white"};
  ${variableSliceAdjacentSliceRules}
`;

const wrapperStyle = css`
  margin: 0 auto;
  text-align: center;
  align-items: stretch;
  display: flex;
  flex-direction: column;
  ${media.tablet`
    text-align: left;
    max-width: 534px;
  `}
  ${media.desktop`
    flex-direction: ${({ reversed }) => (reversed ? "row-reverse" : "row")};
    align-items: center;
    justify-content: space-between;
    max-width: 1140px;
    div:${({ reversed }) => (reversed ? "first-child" : "last-child")} {
      margin-right: calc(100% * 1 / 12);
    }
  `};
`;

const imageWrapperStyle = css`
  ${media.desktop`
    width: calc((100% / 12 * 5 ) - 20px);
    box-sizing: border-box;
  `};
`;

const imageStyle = css`
  width: 100%;
`;

const contentWrapperStyle = css`
  margin-top: 20px;
  ${media.desktop`
    width: calc((100% / 12 * 5 ) - 20px);
    box-sizing: border-box;
    margin-top: 0px;
    & > *:first-child {
      margin-top: 0;
    }
  `};
`;

const headingStyle = css`
  ${regularHeading}
  white-space: pre-wrap;
  margin: 10px 0 0;
  color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.sand : "inherit"};
  ${media.tablet`
    ${largeHeading}
    max-width: 100%;
    color: ${({ darkMode, theme }) =>
      darkMode ? theme.palette.sand : "inherit"};
  `}
`;

const headingLabelStyle = css`
  ${sectionHeading}
  margin: 10px 0 0;
  color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.sand80 : "inherit"};
`;

const paragraphStyle = css`
  ${mediumParagraph}
  margin-top: 20px;
  margin-bottom: 0px;
  color: ${({ darkMode, theme }) =>
    darkMode ? theme.palette.sand : "inherit"};
`;

const buttonStyles = css`
  margin-top: 30px;
  ${media.tablet`
    margin-top: 20px;
`}
  ${media.desktop`
  margin-top: 30px;
`}
`;

const iconWrapperStyle = css`
  display: none;
  ${media.desktop`
    display: block;
  `};
`;

const iconStyle = css`
  width: 100px;
`;

type Props = {
  image: PrismicImage,
  icon?: PrismicImage,
  heading: string,
  reversed: "Yes" | "No",
  darkMode: "Yes" | "No",
  buttonText?: string,
  buttonStyle: "accent" | "primary" | "standard",
  buttonLink?: PrismicLink,
  headingLabel?: string,
  multipleParagraphs: PrismicRichText,
};

export const HalfImageHalfText = ({
  image,
  icon,
  heading,
  reversed,
  darkMode,
  buttonText,
  buttonStyle,
  buttonLink,
  headingLabel,
  multipleParagraphs,
}: Props) => {
  const darkModeBool = darkMode === "Yes";
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0,
  });
  return (
    <div
      ref={ref}
      css={backgroundStyle}
      darkMode={darkModeBool}
      className={`optional-top-padding ${
        darkModeBool ? "darkModeOn" : "darkModeOff"
      }`}
    >
      <div reversed={reversed === "Yes"} css={wrapperStyle}>
        <div css={imageWrapperStyle}>
          <ResponsiveImage
            inView={inView}
            css={imageStyle}
            src={image.url}
            alt={image?.alt}
          />
        </div>
        <div css={contentWrapperStyle}>
          {icon && icon.url && (
            <div showOnDesktop={Boolean(icon)} css={iconWrapperStyle}>
              <ResponsiveImage
                inView={inView}
                css={iconStyle}
                src={icon.url}
                alt={icon?.alt}
              />
            </div>
          )}
          {headingLabel && (
            <p darkMode={darkModeBool} css={headingLabelStyle}>
              {headingLabel}
            </p>
          )}
          <h3 css={headingStyle} darkMode={darkModeBool}>
            {heading}
          </h3>
          {multipleParagraphs.map(({ text }, index) => (
            <p css={paragraphStyle} darkMode={darkModeBool} key={index}>
              {text}
            </p>
          ))}
          {buttonText && buttonLink && (
            <ButtonLink
              type={buttonStyle}
              to={buttonLink?.url}
              css={buttonStyles}
              target={buttonLink?.target}
            >
              {buttonText}
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  );
};
